/** C00 contract v1. Epochs are real instants; calendar fields are explicitly JST. */
export const JST_OFFSET = 9 * 60 * 60 * 1000;
export const MAX_CLOCK_AGE = 60_000;
export class BusinessTimeUnavailable extends Error {}

export function parseServerTime(value: unknown): number {
  const response = value as { success?: unknown; data?: { arr?: unknown; date?: unknown; time?: unknown } } | null;
  const a = response?.data?.arr;
  if (response?.success !== true || !Array.isArray(a) || a.length !== 6 ||
      a.some(v => !/^[0-9]{1,4}$/.test(String(v)))) throw new BusinessTimeUnavailable('Invalid server time');
  const [year, month, day, hour, minute, second] = a.map(Number);
  const wall = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (year < 2000 || year > 2200 || wall.getUTCFullYear() !== year || wall.getUTCMonth() !== month - 1 ||
      wall.getUTCDate() !== day || wall.getUTCHours() !== hour || wall.getUTCMinutes() !== minute || wall.getUTCSeconds() !== second) {
    throw new BusinessTimeUnavailable('Invalid server calendar date');
  }
  const date = wall.toISOString().slice(0, 10), time = wall.toISOString().slice(11, 19);
  if (response.data?.date !== date || response.data?.time !== time) throw new BusinessTimeUnavailable('Inconsistent server time');
  return wall.getTime() - JST_OFFSET; // The legacy timestamp/now fields are shifted; never use them as UTC.
}

export class ServerTime {
  private sample: { epoch: number; monotonic: number; uncertainty: number } | null = null;
  constructor(private readonly elapsed: () => number = () => performance.now()) {}
  accept(epoch: number, requestStarted = this.elapsed()): void {
    const received = this.elapsed();
    if (!Number.isFinite(epoch) || received < requestStarted || received - requestStarted > 5000) throw new BusinessTimeUnavailable('Uncertain server time');
    this.sample = { epoch, monotonic: received, uncertainty: received - requestStarted + 1000 };
  }
  get ready(): boolean { return this.sample !== null; }
  get age(): number { return this.sample ? Math.max(0, this.elapsed() - this.sample.monotonic) : Infinity; }
  get fresh(): boolean { return this.ready && this.age < MAX_CLOCK_AGE; }
  get uncertainty(): number { return this.sample?.uncertainty ?? Infinity; }
  now(): number {
    if (!this.sample) throw new BusinessTimeUnavailable('Server time is not available');
    return this.sample.epoch + this.age;
  }
  clear(): void { this.sample = null; }
}
export const serverTime = new ServerTime();

/** A Date whose calendar access/arithmetic is JST. Serialization still emits the real UTC instant.
 * It is explicit at call sites; native Date and the OS clock are never patched. */
export class JstDate extends Date {
  constructor();
  constructor(value: string | number | Date | undefined | null);
  constructor(year: number, month: number, day?: number, hour?: number, minute?: number, second?: number, ms?: number);
  constructor(...args: any[]) {
    let epoch: number;
    if (!args.length) epoch = serverTime.now();
    else if (args.length > 1) {
      const [y, m, d = 1, h = 0, min = 0, s = 0, ms = 0] = args;
      epoch = Date.UTC(y, m, d, h, min, s, ms) - JST_OFFSET;
    } else {
      const value = args[0];
      const match = typeof value === 'string' && /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/.exec(value);
      epoch = match ? Date.UTC(+match[1], +match[2] - 1, +match[3], +(match[4] || 0), +(match[5] || 0), +(match[6] || 0), Number((match[7] || '').padEnd(3, '0'))) - JST_OFFSET
        : new Date(value).getTime();
    }
    super(epoch);
  }
  private wall(): Date { return new Date(this.getTime() + JST_OFFSET); }
  override getFullYear(): number { return this.wall().getUTCFullYear(); }
  override getMonth(): number { return this.wall().getUTCMonth(); }
  override getDate(): number { return this.wall().getUTCDate(); }
  override getDay(): number { return this.wall().getUTCDay(); }
  override getHours(): number { return this.wall().getUTCHours(); }
  override getMinutes(): number { return this.wall().getUTCMinutes(); }
  override getSeconds(): number { return this.wall().getUTCSeconds(); }
  override getMilliseconds(): number { return this.wall().getUTCMilliseconds(); }
  override getTimezoneOffset(): number { return -540; }
  override setFullYear(y: number, m = this.getMonth(), d = this.getDate()): number { return this.setTime(this.wall().setUTCFullYear(y, m, d) - JST_OFFSET); }
  override setMonth(m: number, d = this.getDate()): number { return this.setTime(this.wall().setUTCMonth(m, d) - JST_OFFSET); }
  override setDate(d: number): number { return this.setTime(this.wall().setUTCDate(d) - JST_OFFSET); }
  override setHours(h: number, m = this.getMinutes(), s = this.getSeconds(), ms = this.getMilliseconds()): number { return this.setTime(this.wall().setUTCHours(h, m, s, ms) - JST_OFFSET); }
  override setMinutes(m: number, s = this.getSeconds(), ms = this.getMilliseconds()): number { return this.setTime(this.wall().setUTCMinutes(m, s, ms) - JST_OFFSET); }
  override setSeconds(s: number, ms = this.getMilliseconds()): number { return this.setTime(this.wall().setUTCSeconds(s, ms) - JST_OFFSET); }
  override setMilliseconds(ms: number): number { return this.setTime(this.wall().setUTCMilliseconds(ms) - JST_OFFSET); }
  override toLocaleDateString(locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions): string { return super.toLocaleDateString(locales, { timeZone: 'Asia/Tokyo', ...options }); }
  override toLocaleString(locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions): string { return super.toLocaleString(locales, { timeZone: 'Asia/Tokyo', ...options }); }
  override toLocaleTimeString(locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions): string { return super.toLocaleTimeString(locales, { timeZone: 'Asia/Tokyo', ...options }); }
}
export function businessPeriod(epoch?: number): string {
  if (!serverTime.ready) return '';
  const d = new JstDate(epoch ?? serverTime.now());
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}/${d.getHours() < 6 ? 'before6' : d.getHours() < 21 ? 'after6' : 'after21'}`;
}
export interface ClockView { period: string; changed: boolean; allowReconfirm: boolean; checking?: boolean; }
export function clockView(allowReconfirm = true): ClockView { return { period: businessPeriod(), changed: false, allowReconfirm }; }
